import type { FileInfo, StandardResponse, StreamInput } from "@pdfking/io"
import { ResponseCreate, StreamToIterable } from "@pdfking/io"
import { Floor } from "@pdfking/math"
import type { Bytes, FileName } from "@pdfking/structs"
import { ContentType, NullQ } from "@pdfking/structs"
import type { AsyncSequence } from "@pdfking/tfunc"
import { SafePromise, UnixTime } from "@pdfking/tfunc"

import type { FileQuery, FileSystem } from "#public"

import { EntryKey } from "./EntryKey"
import { FolderMemberQ } from "./FolderMemberQ"
import { JoinPath } from "./JoinPath"
import { SplitPath } from "./SplitPath"

// What the directory entry already says, over bytes nobody has read: a File is
// a Blob, and a Blob in a Response is not touched until the body is — so a
// listing of a whole tree opens nothing.
const Read = async (key: FileName, handle: FileSystemFileHandle): Promise<StandardResponse> => {
  const file = await handle.getFile()

  return ResponseCreate(file, {
    modified: Floor(UnixTime(new Date(file.lastModified))),
    name: key,
    size: file.size as Bytes,
    // What the platform made of the name — a File has no type of its own
    // beyond that guess, and an empty one is the guess having failed.
    type: (file.type as ContentType) || ContentType.BYTES,
  })
}

/** A filesystem over a directory handle, whoever granted it.
 *
 * Keys are paths relative to that directory: "a/b.txt" reads through the
 * nested handles, and writing one creates the directories on the way. The
 * handle is the only thing that differs between the storages built on this —
 * one the user picked, or a folder of the origin's own private one — and
 * permission is settled before anything gets here, so nothing below deals with
 * grants.
 *
 * The root arrives as a thunk rather than a handle because a directory can
 * stop existing under a filesystem that still does: OPFS drops its whole
 * folder on `clear`, and the next call has to resolve a live one. Which is
 * also why `clear` here empties the root rather than removing it — the one
 * handle nobody can remake is the one the user picked. */
export const ImplementHandleFileSystem = (
  Root: () => Promise<FileSystemDirectoryHandle>
): FileSystem => {
  // Missing directory is an answer, not a failure: `get`/`exists`/`list` all
  // need "not there" rather than a NotFoundError.
  const Resolve = async (parts: string[]): Promise<FileSystemDirectoryHandle | undefined> => {
    let handle = await Root()
    for (const part of parts) {
      const { error, value } = await SafePromise(handle.getDirectoryHandle(part))
      if (error || !value) {
        return undefined
      }
      handle = value
    }
    return handle
  }

  // The writing counterpart: every failure here is real (no grant, bad name).
  const Ensure = async (parts: string[]): Promise<FileSystemDirectoryHandle> => {
    let handle = await Root()
    for (const part of parts) {
      handle = await handle.getDirectoryHandle(part, { create: true })
    }
    return handle
  }

  const Split = (path: FileName): { name: string; parts: string[] } => {
    const parts = SplitPath(path)
    const name = parts.pop()
    if (NullQ(name)) {
      throw new Error(`"${path}" is a directory, not a file`)
    }
    return { name, parts }
  }

  const File = async (path: FileName): Promise<FileSystemFileHandle | undefined> => {
    const { name, parts } = Split(path)
    const directory = await Resolve(parts)
    if (!directory) {
      return undefined
    }
    const { error, value } = await SafePromise(directory.getFileHandle(name))
    return error ? undefined : value
  }

  // Files only — a directory holds no bytes, so it is not a key of this
  // filesystem. Which entries count is the same rule every store here uses.
  // The handles the iterator yields are the ones read: nothing walks back to
  // the root to re-resolve a path it is already holding.
  async function* Walk(
    directory: FileSystemDirectoryHandle,
    folder: FileName,
    query: FileQuery
  ): AsyncGenerator<[FileName, StandardResponse]> {
    for await (const child of directory.values()) {
      const path = JoinPath(folder, child.name) as FileName

      if (child.kind === "directory") {
        if (query.deep ?? true) yield* Walk(child, path, query)
        continue
      }

      if (!FolderMemberQ(query, path)) continue
      yield [path, await Read(path, child)]
    }
  }

  // A stream into a file that may not exist yet, through the directories on
  // the way — chunk by chunk, so the content is never held whole here. Needs
  // the grant to have been `mode: "readwrite"`, otherwise `createWritable`
  // rejects with a NotAllowedError. The writable is a staging area of the
  // API's own: nothing lands on the real file until `close`, so a reader
  // mid-write still finds the previous content, whole.
  const Write = async (key: FileName, value: StreamInput): Promise<void> => {
    const { name, parts } = Split(key)
    const directory = await Ensure(parts)
    const handle = await directory.getFileHandle(name, { create: true })
    const writable = await handle.createWritable()
    try {
      for await (const chunk of StreamToIterable(value)) {
        // The chunks a stream yields are ArrayBuffer-backed; the API's type
        // just insists on hearing it.
        await writable.write(chunk as Uint8Array<ArrayBuffer>)
      }
    } finally {
      await writable.close()
    }
  }

  return {
    async clear(): Promise<void> {
      const root = await Root()
      for await (const name of root.keys()) {
        await root.removeEntry(name, { recursive: true })
      }
    },

    // The source's File straight into the dest's writable — as the stream,
    // not the Blob: a Blob handed over whole is read whole, and the stream is
    // pulled a chunk at a time.
    async copy(source: FileName, dest: FileName): Promise<void> {
      const handle = await File(source)
      if (!handle) throw new Error(`no such file: ${source}`)
      await Write(dest, (await handle.getFile()).stream())
    },

    async delete(key: FileName): Promise<void> {
      const { name, parts } = Split(key)
      const directory = await Resolve(parts)
      if (directory) {
        await SafePromise(directory.removeEntry(name, { recursive: true }))
      }
    },

    async exists(key: FileName): Promise<boolean> {
      return !!(await File(key))
    },

    async get(key: FileName): Promise<StandardResponse | undefined> {
      const handle = await File(key)
      return handle && Read(EntryKey(key), handle)
    },

    // The whole tree from the folder asked for, every file keyed by its path
    // from the root — directories are not keys, they hold no bytes. A folder
    // nobody made holds nothing: an empty answer, not a failure.
    async *list({
      deep = true,
      hidden = false,
      path = "" as FileName,
    }: FileQuery = {}): AsyncSequence<[FileName, StandardResponse]> {
      const parts = SplitPath(path)
      const directory = await Resolve(parts)
      if (directory) yield* Walk(directory, JoinPath(...parts) as FileName, { deep, hidden, path })
    },

    async move(source: FileName, dest: FileName): Promise<void> {
      await this.copy(source, dest)
      await this.delete(source)
    },

    async set(key: FileName, value: StreamInput, type?: ContentType): Promise<FileInfo> {
      await Write(key, value)

      // The mtime is the file's, not the one this write stamped: a reader
      // derives the etag off the directory entry, so a writer reporting
      // anything else would be reporting one nobody else derives. The type is
      // the caller's — a handle holds bytes and a name and nothing else, so a
      // reader has only the extension to guess it back from.
      const written = await this.get(key)
      if (!written) throw new Error(`file vanished on write: ${key}`)
      return type ? { ...written.info, type } : written.info
    },
  }
}
